import './style.scss';

declare global {
  interface Window {
    __data__: any;
    __store__: any;
  }
}
// prepare store

declare global {
  interface Window {
    env: any;
    __root__: any;
  }
}

import { Buffer } from 'buffer';
import * as Sentry from '@sentry/react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { ThemeProvider } from '@mui/material/styles';
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { amplifyConfigure } from 'utils/amplify/amplify';
import { filterSentryApiErrorEvent } from 'utils/apiError';
import { theme } from 'utils/styles';
import ApiClient from './ApiClient';
import App from './App';
import Config from './config/config';
import { LaunchDarklyProvider } from './providers/LaunchDarklyProvider';
import SessionTimeoutProvider from './providers/SessionTimeoutProvider';
import { hydrateStartThunk } from './redux/hydration/hydrationThunks';
import { createStore } from './redux/store';

// Set up Buffer for browser
window.Buffer = Buffer;

// Start Bugsnag
Bugsnag.start({
  apiKey: Config.getBugSnagKey(),
  plugins: [new BugsnagPluginReact()],
  appVersion: Config.getAppVersion(),
  releaseStage: Config.getDeploymentEnv(),
  enabledReleaseStages: ['production', 'staging']
});

Sentry.init({
  dsn: Config.getSentryDsn(),
  environment: Config.getDeploymentEnv(),
  sendDefaultPii: true,
  enabled: ['production', 'staging'].includes(Config.getDeploymentEnv()),
  integrations: [Sentry.extraErrorDataIntegration()],
  beforeSend: filterSentryApiErrorEvent
});

amplifyConfigure(Config.getDeploymentEnv());

// Get ErrorBoundary from Bugsnag
const ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React);

// Get the root DOM node
const dest: any = document.getElementById('content');

// Prepare Redux store
const apiClient = ApiClient.getInstance();
const store = createStore(apiClient);
if (!window?.__store__) {
  apiClient.setStore(store);
  window.__store__ = store;
}

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch | any;

// Set CSRF token and clean up
const serverData = window?.__data__ || {};
apiClient.setCsrfToken(serverData.CSRF_TOKEN);
console.log('CSRF_TOKEN', serverData.CSRF_TOKEN);
window.__data__.CSRF_TOKEN = undefined;
window.__data__.store = undefined;

// Hydrate initial Redux state
store.dispatch(hydrateStartThunk());

// Mount the app at the top level, LaunchDarklyProvider is a stable import
let root = window.__root__;
if (!root) {
  root = hydrateRoot(
    dest,
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <LaunchDarklyProvider>
          <ErrorBoundary>
            <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
              <SessionTimeoutProvider>
                <App />
              </SessionTimeoutProvider>
            </Sentry.ErrorBoundary>
          </ErrorBoundary>
        </LaunchDarklyProvider>
      </ThemeProvider>
    </Provider>
  );
  window.__root__ = root;
} else {
  root.render(
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <LaunchDarklyProvider>
          <ErrorBoundary>
            <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
              <SessionTimeoutProvider>
                <App />
              </SessionTimeoutProvider>
            </Sentry.ErrorBoundary>
          </ErrorBoundary>
        </LaunchDarklyProvider>
      </ThemeProvider>
    </Provider>
  );
}

// Hot Module Replacement (HMR) for development
if (process.env.NODE_ENV === 'development' && module.hot) {
  module.hot.accept('./App', () => {
    const NewApp = require('./App').default;
    root.render(
      <Provider store={store}>
        <ThemeProvider theme={theme}>
          <LaunchDarklyProvider>
            <ErrorBoundary>
              <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
                <SessionTimeoutProvider>
                  <NewApp />
                </SessionTimeoutProvider>
              </Sentry.ErrorBoundary>
            </ErrorBoundary>
          </LaunchDarklyProvider>
        </ThemeProvider>
      </Provider>
    );
  });
}
